import { supabase, TABLES } from './supabase'
import { computeApexAccountLevel } from './apex'

/** `games.name` for Apex — the only game with per-player stat badges today. */
const APEX_GAME_SLUG = 'apex-legends'

/** A player's standing in a tournament's registration list. */
export type RegistrationStatus = 'none' | 'confirmed' | 'substitute'

export interface RegistrationStanding {
  /** All registrations, regardless of confirmed/substitute. */
  total: number
  /** Confirmed-spot capacity (teams × players per team). */
  capacity: number
  /** Players holding a confirmed spot (== min(total, capacity)). */
  confirmed: number
  /** Players over capacity, registered as substitutes. */
  substitutes: number
  /** The current user's own standing. */
  myStatus: RegistrationStatus
}

/**
 * Live registration standing for a tournament.
 *
 * Backed by the `tournament_registration_stats` SECURITY DEFINER function: the
 * table's RLS only lets a user read their own row, so aggregate counts must come
 * from the function (which returns only counts + the caller's own status).
 */
export async function getRegistrationStanding(
  tournamentId: string
): Promise<RegistrationStanding | null> {
  const { data, error } = await supabase
    .rpc('tournament_registration_stats', { p_tournament_id: tournamentId })
    .maybeSingle()

  if (error || !data) {
    if (error) console.error('getRegistrationStanding error:', error.message)
    return null
  }

  const row = data as {
    total: number
    capacity: number
    confirmed: number
    substitutes: number
    my_status: string
  }
  return {
    total: row.total,
    capacity: row.capacity,
    confirmed: row.confirmed,
    substitutes: row.substitutes,
    myStatus: (row.my_status as RegistrationStatus) ?? 'none',
  }
}

/** Compact summary for a tournament list card. */
export interface TournamentCardSummary {
  total: number
  isRegistered: boolean
  preview: { gamertag: string; avatar: string | null }[]
}

/** Batch fetch card summaries for many tournaments in one round-trip. */
export async function getTournamentCardSummaries(
  ids: string[]
): Promise<Map<string, TournamentCardSummary>> {
  const out = new Map<string, TournamentCardSummary>()
  if (ids.length === 0) return out
  const { data, error } = await supabase.rpc('tournament_card_summaries', { p_ids: ids })
  if (error || !data) {
    if (error) console.error('getTournamentCardSummaries error:', error.message)
    return out
  }
  for (const row of data as Array<{
    tournament_id: string
    total: number
    is_registered: boolean
    preview: { gamertag: string; avatar: string | null }[] | null
  }>) {
    out.set(row.tournament_id, {
      total: row.total,
      isRegistered: row.is_registered,
      preview: row.preview ?? [],
    })
  }
  return out
}

/** A tournament a user has registered for. */
export interface UserTournament {
  slug: string
  title: string
  startsAt: string
  status: string
  coverImage: string | null
}

/** Tournaments the given user has registered for, soonest first. */
export async function getUserTournaments(userId: string): Promise<UserTournament[]> {
  const { data, error } = await supabase.rpc('user_tournaments', { p_user_id: userId })
  if (error || !data) {
    if (error) console.error('getUserTournaments error:', error.message)
    return []
  }
  return (data as Array<{
    slug: string
    title: string
    starts_at: string
    status: string
    cover_image: string | null
  }>).map((r) => ({
    slug: r.slug,
    title: r.title,
    startsAt: r.starts_at,
    status: r.status,
    coverImage: r.cover_image,
  }))
}

/** Whoever referred this participant onto Gamerplug. */
export interface ParticipantReferrer {
  gamertag: string
  avatar: string | null
}

/** A participant's Apex Legends standing, when they've linked the game. */
export interface ParticipantApex {
  /** Effective account level (prestige folded in). */
  level: number | null
  /** Ranked tier name, e.g. "Diamond". */
  rankName: string | null
  /** Division within the tier, e.g. "2". */
  rankDiv: string | null
  /** Rank badge image URL from the stats provider. */
  rankImg: string | null
  /** Ranked points (RP) — the sortable metric behind the event leaderboard. */
  rankScore: number | null
}

/** A single participant in registration order. */
export interface Participant {
  rank: number
  userId: string
  gamertag: string
  avatar: string | null
  status: 'confirmed' | 'substitute' | 'banned'
  registeredAt: string
  /** Banned by a host from this event: no lobby code, no chat posts. */
  banned: boolean
  /** Set when this player joined via someone's referral. */
  referrer: ParticipantReferrer | null
  /** Set when this player has a linked Apex Legends profile. */
  apex: ParticipantApex | null
}

/**
 * Fetch referral + Apex badge data for the given participant user IDs.
 *
 * Apex rank/level badges only exist for Apex events — `apex_profiles` is
 * populated by the apex-sync edge function and has no equivalent for other
 * games — so the profile join is skipped entirely unless this is an Apex
 * tournament. Referral badges are game-agnostic and always fetched.
 */
async function getParticipantBadges(
  userIds: string[],
  gameSlug?: string
): Promise<{
  referrers: Map<string, ParticipantReferrer>
  apex: Map<string, ParticipantApex>
}> {
  const referrers = new Map<string, ParticipantReferrer>()
  const apex = new Map<string, ParticipantApex>()
  if (userIds.length === 0) return { referrers, apex }

  const wantsApex = gameSlug === undefined || gameSlug === APEX_GAME_SLUG

  const [usersRes, gamesRes] = await Promise.all([
    supabase
      .from(TABLES.USERS)
      .select('id, referred_by_user_id')
      .in('id', userIds),
    wantsApex
      ? supabase
          .from(TABLES.USER_GAMES)
          .select(
            'user_id, games!inner(name), apex_profiles(level, level_prestige, account_level, br_rank_name, br_rank_div, br_rank_img, br_rank_score)'
          )
          .in('user_id', userIds)
          .eq('games.name', APEX_GAME_SLUG)
      : Promise.resolve({ data: null }),
  ])

  // Resolve each referred player's referrer profile (gamertag + avatar).
  const referredBy = new Map<string, string>()
  const referrerIds = new Set<string>()
  for (const row of (usersRes.data ?? []) as Array<{
    id: string
    referred_by_user_id: string | null
  }>) {
    if (row.referred_by_user_id) {
      referredBy.set(row.id, row.referred_by_user_id)
      referrerIds.add(row.referred_by_user_id)
    }
  }
  if (referrerIds.size > 0) {
    const { data: refData } = await supabase
      .from(TABLES.USERS)
      .select('id, gamertag, profile_image_url')
      .in('id', [...referrerIds])
    const byId = new Map(
      ((refData ?? []) as Array<{
        id: string
        gamertag: string
        profile_image_url: string | null
      }>).map((r) => [r.id, r])
    )
    for (const [userId, refId] of referredBy) {
      const ref = byId.get(refId)
      if (ref) referrers.set(userId, { gamertag: ref.gamertag, avatar: ref.profile_image_url })
    }
  }

  // One Apex profile per linked user_game; fold prestige into the level.
  for (const row of (gamesRes.data ?? []) as Array<{
    user_id: string
    apex_profiles:
      | {
          level: number | null
          level_prestige: number | null
          account_level: number | null
          br_rank_name: string | null
          br_rank_div: number | string | null
          br_rank_img: string | null
          br_rank_score: number | null
        }
      | Array<unknown>
      | null
  }>) {
    const p = Array.isArray(row.apex_profiles) ? row.apex_profiles[0] : row.apex_profiles
    if (!p) continue
    const profile = p as {
      level: number | null
      level_prestige: number | null
      account_level: number | null
      br_rank_name: string | null
      br_rank_div: number | string | null
      br_rank_img: string | null
      br_rank_score: number | null
    }
    const div =
      profile.br_rank_div === null || profile.br_rank_div === undefined || profile.br_rank_div === ''
        ? null
        : String(profile.br_rank_div)
    apex.set(row.user_id, {
      level: computeApexAccountLevel(profile),
      rankName: profile.br_rank_name ?? null,
      rankDiv: div,
      rankImg: profile.br_rank_img ?? null,
      rankScore: typeof profile.br_rank_score === 'number' ? profile.br_rank_score : null,
    })
  }

  return { referrers, apex }
}

/**
 * Full participant list for a tournament, in registration order (rank 1 first).
 * Backed by the `tournament_participants` SECURITY DEFINER function, then
 * enriched with referral + Apex badge data.
 */
export async function getTournamentParticipants(
  tournamentId: string,
  gameSlug?: string
): Promise<Participant[]> {
  const { data, error } = await supabase.rpc('tournament_participants', {
    p_tournament_id: tournamentId,
  })

  if (error || !data) {
    if (error) console.error('getTournamentParticipants error:', error.message)
    return []
  }

  const base = (data as Array<{
    rank: number
    user_id: string
    gamertag: string
    profile_image_url: string | null
    status: string
    registered_at: string
    banned: boolean | null
  }>).map((r) => ({
    rank: r.rank,
    userId: r.user_id,
    gamertag: r.gamertag,
    avatar: r.profile_image_url,
    status: (r.status === 'banned'
      ? 'banned'
      : r.status === 'substitute'
        ? 'substitute'
        : 'confirmed') as 'confirmed' | 'substitute' | 'banned',
    registeredAt: r.registered_at,
    banned: r.banned ?? false,
  }))

  const { referrers, apex } = await getParticipantBadges(
    base.map((p) => p.userId),
    gameSlug
  )

  return base.map((p) => ({
    ...p,
    referrer: referrers.get(p.userId) ?? null,
    apex: apex.get(p.userId) ?? null,
  }))
}

/**
 * Ban a player from an event (organizer only). The registration row is left
 * alone — the ban blocks the lobby code and group-chat posts at the database,
 * and the roster keeps showing them as banned.
 */
export async function banTournamentPlayer(
  tournamentId: string,
  userId: string,
  reason?: string
): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase.rpc('ban_tournament_player', {
    p_tournament_id: tournamentId,
    p_user_id: userId,
    p_reason: reason?.trim() || null,
  })
  if (error) {
    console.error('banTournamentPlayer error:', error.message)
    return { ok: false, error: error.message }
  }
  return { ok: true }
}

/** Lift a ban (organizer only). */
export async function unbanTournamentPlayer(
  tournamentId: string,
  userId: string
): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase.rpc('unban_tournament_player', {
    p_tournament_id: tournamentId,
    p_user_id: userId,
  })
  if (error) {
    console.error('unbanTournamentPlayer error:', error.message)
    return { ok: false, error: error.message }
  }
  return { ok: true }
}

/** A ban as seen by the banned player themselves. */
export interface MyTournamentBan {
  reason: string | null
  createdAt: string
}

/**
 * The current user's own ban for a tournament, or null when they aren't banned.
 * Readable under the "Players can see their own ban" policy, so the chat and
 * lobby-code UI can explain the restriction instead of failing silently.
 */
export async function getMyTournamentBan(
  tournamentId: string,
  userId: string
): Promise<MyTournamentBan | null> {
  const { data, error } = await supabase
    .from('tournament_bans')
    .select('reason, created_at')
    .eq('tournament_id', tournamentId)
    .eq('user_id', userId)
    .maybeSingle()

  if (error) {
    console.error('getMyTournamentBan error:', error.message)
    return null
  }
  if (!data) return null
  return { reason: data.reason, createdAt: data.created_at }
}
